import { 
  signInWithPhoneNumber,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateEmail as fbUpdateEmail,
  updatePassword as fbUpdatePassword,
  updateProfile as fbUpdateProfile,
  reauthenticateWithCredential,
  EmailAuthProvider,
  type ConfirmationResult,
  type User as FirebaseUser
} from 'firebase/auth'
import { doc, getDoc, setDoc, updateDoc, deleteDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore'
import { auth, db } from './config'
import type { User, UserRole } from '@/types'
import { COLLECTIONS } from '@/constants'

class AuthService {
  private confirmationResult: ConfirmationResult | null = null

  /**
   * Send OTP to phone number
   */
  async sendOTP(phoneNumber: string, appVerifier: unknown): Promise<ConfirmationResult> {
    if (!auth) throw new Error('Firebase is not configured. Please contact your administrator.')
    const formattedPhone = phoneNumber.startsWith('+91') ? phoneNumber : `+91${phoneNumber}`
    this.confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, appVerifier as any)
    return this.confirmationResult
  }

  /**
   * Sign in with email and password
   */
  async loginWithEmail(email: string, password: string): Promise<FirebaseUser> {
    if (!auth) throw new Error('Firebase is not configured. Please contact your administrator.')
    const result = await signInWithEmailAndPassword(auth, email, password)
    return result.user
  }

  /**
   * Verify OTP code
   */
  async verifyOTP(code: string): Promise<FirebaseUser> {
    if (!this.confirmationResult) {
      throw new Error('No verification in progress. Please request OTP again.')
    }
    const result = await this.confirmationResult.confirm(code)
    return result.user
  }

  /**
   * Get user profile from Firestore
   */
  async getUserProfile(uid: string, phoneOrEmail?: string): Promise<User | null> {
    if (!db) return null

    // Primary lookup: by UID (doc ID)
    const byUid = await getDoc(doc(db, COLLECTIONS.USERS, uid))
    if (byUid.exists()) {
      const data = byUid.data()
      let engineerDocId = data.engineerDocId

      // Dynamic self-healing: if role is engineer but engineerDocId is missing, resolve it
      if (!engineerDocId && data.role === 'engineer') {
        try {
          // 1. Try finding by auth UID
          const eq = query(collection(db, COLLECTIONS.ENGINEERS), where('uid', '==', uid))
          const esnap = await getDocs(eq)
          if (!esnap.empty) {
            engineerDocId = esnap.docs[0].id
            await updateDoc(doc(db, COLLECTIONS.USERS, uid), { engineerDocId })
          } else {
            // 2. Try finding by email or phone
            const phone = data.phoneNumber
            const email = data.email
            let eq2 = null
            if (email) {
              eq2 = query(collection(db, COLLECTIONS.ENGINEERS), where('email', '==', email))
            } else if (phone) {
              const normalized = phone.startsWith('+91') ? phone : `+91${phone}`
              eq2 = query(collection(db, COLLECTIONS.ENGINEERS), where('phoneNumber', '==', normalized))
            }
            if (eq2) {
              const esnap2 = await getDocs(eq2)
              if (!esnap2.empty) {
                engineerDocId = esnap2.docs[0].id
                await updateDoc(doc(db, COLLECTIONS.USERS, uid), { engineerDocId })
                // Also link the auth UID on the engineer document
                await updateDoc(esnap2.docs[0].ref, { uid })
              }
            }
          }
        } catch (err) {
          console.warn('[Auth] Error resolving missing engineerDocId:', err)
        }
      }

      return {
        id: byUid.id,
        uid: data.uid || uid,
        phoneNumber: data.phoneNumber,
        name: data.name,
        email: data.email,
        role: data.role as UserRole,
        avatar: data.avatar,
        isActive: data.isActive ?? true,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        lastLoginAt: data.lastLoginAt?.toDate(),
        assignedZone: data.assignedZone,
        employeeId: data.employeeId,
        engineerDocId
      }
    }

    // Fallback: look up by email or phone number (for pre-registered users created by admin)
    if (phoneOrEmail) {
      let q
      if (phoneOrEmail.includes('@')) {
        q = query(collection(db, COLLECTIONS.USERS), where('email', '==', phoneOrEmail.trim()))
      } else {
        const normalized = phoneOrEmail.startsWith('+91') ? phoneOrEmail : `+91${phoneOrEmail}`
        q = query(collection(db, COLLECTIONS.USERS), where('phoneNumber', '==', normalized))
      }

      const snap = await getDocs(q)
      if (!snap.empty) {
        const preDoc = snap.docs[0]
        const data = preDoc.data()
        
        let engineerDocId = data.engineerDocId

        // Claim this doc — write the real UID as document ID, then delete old doc
        await setDoc(doc(db, COLLECTIONS.USERS, uid), {
          ...data,
          uid,
          updatedAt: serverTimestamp()
        })
        await deleteDoc(preDoc.ref)

        // Dynamic self-healing: if role is engineer but engineerDocId is missing, resolve it
        if (!engineerDocId && data.role === 'engineer') {
          try {
            const phone = data.phoneNumber
            const email = data.email
            let eq2 = null
            if (email) {
              eq2 = query(collection(db, COLLECTIONS.ENGINEERS), where('email', '==', email))
            } else if (phone) {
              const normalized = phone.startsWith('+91') ? phone : `+91${phone}`
              eq2 = query(collection(db, COLLECTIONS.ENGINEERS), where('phoneNumber', '==', normalized))
            }
            if (eq2) {
              const esnap2 = await getDocs(eq2)
              if (!esnap2.empty) {
                engineerDocId = esnap2.docs[0].id
                await updateDoc(doc(db, COLLECTIONS.USERS, uid), { engineerDocId })
                await updateDoc(esnap2.docs[0].ref, { uid })
              }
            }
          } catch (err) {
            console.warn('[Auth] Error resolving missing engineerDocId on claim:', err)
          }
        }

        return {
          id: uid,
          uid,
          phoneNumber: data.phoneNumber,
          name: data.name,
          email: data.email,
          role: data.role as UserRole,
          avatar: data.avatar,
          isActive: data.isActive ?? true,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          lastLoginAt: data.lastLoginAt?.toDate(),
          assignedZone: data.assignedZone,
          employeeId: data.employeeId,
          engineerDocId
        }
      }
    }

    return null
  }

  /**
   * Create or update user profile
   */
  async saveUserProfile(userData: Partial<User> & { uid: string }): Promise<void> {
    if (!db) throw new Error('Firebase is not configured.')
    const userRef = doc(db, COLLECTIONS.USERS, userData.uid)
    const userDoc = await getDoc(userRef)
    
    if (userDoc.exists()) {
      // Update existing user
      await updateDoc(userRef, {
        ...userData,
        updatedAt: serverTimestamp()
      })
    } else {
      // Create new user
      await setDoc(userRef, {
        ...userData,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp()
      })
    }
  }

  /**
   * Update last login timestamp
   */
  async updateLastLogin(uid: string): Promise<void> {
    if (!db) return
    const userRef = doc(db, COLLECTIONS.USERS, uid)
    await updateDoc(userRef, {
      lastLoginAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    if (auth) await signOut(auth)
    this.confirmationResult = null
  }

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChanged(callback: (user: FirebaseUser | null) => void): () => void {
    if (!auth) {
      callback(null)
      return () => {}
    }
    return onAuthStateChanged(auth, callback)
  }

  /**
   * Get current user
   */
  getCurrentUser(): FirebaseUser | null {
    return auth?.currentUser ?? null
  }

  /**
   * Check if user has required role
   */
  hasRole(userRole: UserRole, requiredRoles: UserRole[]): boolean {
    return requiredRoles.includes(userRole)
  }

  /**
   * Check if user is admin
   */
  isAdmin(userRole: UserRole): boolean {
    return userRole === 'admin'
  }

  /**
   * Check if user is engineer
   */
  isEngineer(userRole: UserRole): boolean {
    return userRole === 'engineer'
  }

  /**
   * Check if user is supervisor
   */
  isSupervisor(userRole: UserRole): boolean {
    return userRole === 'supervisor'
  }

  /**
   * Update Firestore user profile + Firebase displayName
   */
  async updateUserProfile(uid: string, updates: { name?: string; email?: string; phoneNumber?: string; assignedZone?: string; employeeId?: string }): Promise<void> {
    if (!db) throw new Error('Firebase is not configured.')
    const clean = Object.fromEntries(Object.entries(updates).filter(([, v]) => v !== undefined && v !== ''))
    await updateDoc(doc(db, COLLECTIONS.USERS, uid), { ...clean, updatedAt: serverTimestamp() })
    if (auth?.currentUser && updates.name) {
      await fbUpdateProfile(auth.currentUser, { displayName: updates.name })
    }
  }

  /**
   * Re-authenticate the current email/password user (required before sensitive changes)
   */
  async reauthenticate(currentPassword: string): Promise<void> {
    const user = auth?.currentUser
    if (!user || !user.email) throw new Error('No authenticated email user found.')
    const credential = EmailAuthProvider.credential(user.email, currentPassword)
    await reauthenticateWithCredential(user, credential)
  }

  /**
   * Update Firebase Auth email + Firestore record
   */
  async updateUserEmail(uid: string, newEmail: string, currentPassword: string): Promise<void> {
    await this.reauthenticate(currentPassword)
    const user = auth?.currentUser
    if (!user) throw new Error('Not authenticated.')
    await fbUpdateEmail(user, newEmail)
    if (db) await updateDoc(doc(db, COLLECTIONS.USERS, uid), { email: newEmail, updatedAt: serverTimestamp() })
  }

  /**
   * Update Firebase Auth password
   */
  async updateUserPassword(newPassword: string, currentPassword: string): Promise<void> {
    await this.reauthenticate(currentPassword)
    const user = auth?.currentUser
    if (!user) throw new Error('Not authenticated.')
    await fbUpdatePassword(user, newPassword)
  }

  /**
   * Create a new Firebase Auth user with email + default password.
   * Called when admin creates a new user profile with an email.
   * Uses a secondary app instance so that it does not disrupt the current Admin session.
   */
  async createEmailUser(email: string, displayName: string): Promise<string> {
    if (!auth) throw new Error('Firebase is not configured.')
    const DEFAULT_PASSWORD = '123456'

    const { initializeApp, deleteApp } = await import('firebase/app')
    const { getAuth, createUserWithEmailAndPassword, updateProfile, signOut } = await import('firebase/auth')

    const firebaseConfig = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
      measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
    }

    const tempApp = initializeApp(firebaseConfig, 'AdminUserCreation')
    const tempAuth = getAuth(tempApp)

    try {
      const cred = await createUserWithEmailAndPassword(tempAuth, email, DEFAULT_PASSWORD)
      await updateProfile(cred.user, { displayName })
      const newUid = cred.user.uid
      await signOut(tempAuth)
      return newUid
    } finally {
      await deleteApp(tempApp)
    }
  }

  /**
   * Check whether a logged-in user must change their password.
   * Returns true if the Firestore user doc has mustChangePassword === true.
   */
  async checkMustChangePassword(uid: string): Promise<boolean> {
    if (!db) return false
    const snap = await getDoc(doc(db, COLLECTIONS.USERS, uid))
    if (!snap.exists()) return false
    return snap.data().mustChangePassword === true
  }

  /**
   * Admin resets a user's password back to 123456 by setting a Firestore flag.
   * Also ensures a corresponding Firebase Authentication user exists.
   */
  async adminResetPassword(targetEmail: string, targetUid?: string): Promise<void> {
    if (!db) throw new Error('Firebase is not configured.')

    let currentUid = targetUid

    // If the Firebase Auth account hasn't been created yet, attempt to create it
    if (targetEmail) {
      try {
        const tempUid = await this.createEmailUser(targetEmail, 'BSNL User')
        currentUid = tempUid
      } catch (authErr: any) {
        if (authErr.code !== 'auth/email-already-in-use') {
          console.warn('[Auth] Error checking/creating user in Auth during reset:', authErr)
        }
      }
    }

    // Find the user doc by uid or email
    let userDocId = currentUid
    let userDocRef = null

    if (userDocId) {
      userDocRef = doc(db, COLLECTIONS.USERS, userDocId)
    } else if (targetEmail) {
      const q = query(collection(db, COLLECTIONS.USERS), where('email', '==', targetEmail))
      const snap = await getDocs(q)
      if (!snap.empty) {
        userDocRef = snap.docs[0].ref
        userDocId = snap.docs[0].id
      }
    }

    if (!userDocRef) throw new Error('User not found in directory.')

    await updateDoc(userDocRef, {
      ...(userDocId && { uid: userDocId }),
      mustChangePassword: true,
      passwordResetAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })

    // Also sync the uid to the engineer document if not already set
    if (targetEmail && userDocId) {
      const eq = query(collection(db, COLLECTIONS.ENGINEERS), where('email', '==', targetEmail))
      const esnap = await getDocs(eq)
      for (const edoc of esnap.docs) {
        await updateDoc(edoc.ref, {
          uid: userDocId,
          updatedAt: serverTimestamp()
        })
      }
    }
  }

  /**
   * After first login with default password, user sets their own password.
   * Clears the mustChangePassword flag on success.
   */
  async changePasswordFirstTime(uid: string, newPassword: string): Promise<void> {
    const user = auth?.currentUser
    if (!user) throw new Error('Not authenticated.')
    // Re-auth with the default password before changing
    const credential = EmailAuthProvider.credential(user.email!, '123456')
    await reauthenticateWithCredential(user, credential)
    await fbUpdatePassword(user, newPassword)
    if (db) {
      await updateDoc(doc(db, COLLECTIONS.USERS, uid), {
        mustChangePassword: false,
        updatedAt: serverTimestamp(),
      })
    }
  }

  /**
   * Read mustChangePassword from a Firestore user doc snapshot (avoids extra round-trip).
   */
  parseMustChangePassword(data: Record<string, unknown>): boolean {
    return data.mustChangePassword === true
  }
}

export const authService = new AuthService()
export default authService
